import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { apiGet, getAuthHeaders } from '../../lib/api'
import { 
  BookOpen, 
  Plus, 
  ChevronRight,
  Clock,
  CheckCircle,
  Upload,
  Edit3,
  Calendar,
  Link as LinkIcon,
  Download,
  Printer,
  X
} from 'lucide-react'
import AddProfessionModal from '../../components/AddProfessionModal'

interface UserProfession {
  id: string
  professionId: string
  professionName: string
  state: string
  ceRequirements?: {
    totalHours: number
    renewalPeriodMonths: number
  }
  progress?: {
    totalHoursEarned: number
    percentComplete: number
  }
}

interface CEProgressSummary {
  userProfessionId: string
  professionName: string
  totalHoursEarned: number
  totalHoursRequired: number | null
  percentComplete: number
  renewalCycleEndDate: string | null
  renewalCycleStartDate: string | null
  linkedCertificationName: string | null
}

interface CEAllocation {
  userProfessionId: string
  professionName?: string | null
}

interface CERecord {
  id: string
  title: string
  provider: string
  hoursAwarded: string | number | null
  dateCompleted: string | null
  documentUri?: string | null
  source: string
  notes: string | null
  createdAt: string
  allocations?: CEAllocation[]
}

export default function CEsPage() {
  const [showAddMenu, setShowAddMenu] = useState(false)
  const [showAddProfessionModal, setShowAddProfessionModal] = useState(false)
  const [selectionMode, setSelectionMode] = useState<'export' | 'print' | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [isPreparing, setIsPreparing] = useState(false)
  const [professionFilter, setProfessionFilter] = useState<string>('all')

  const { data: professionsData, isLoading: loadingProfessions } = useQuery({
    queryKey: ['/api/user/professions'],
    queryFn: () => apiGet<{ professions: UserProfession[] }>('/user/professions'),
  })

  const { data: ceRecordsData, isLoading: loadingRecords } = useQuery({
    queryKey: ['/api/continuing-education'],
    queryFn: () => apiGet<CERecord[]>('/continuing-education'),
  })

  const { data: progressSummaryData } = useQuery({
    queryKey: ['/api/user/ce-progress-summary'],
    queryFn: () => apiGet<CEProgressSummary[]>('/user/ce-progress-summary'),
  })

  const rawProfessions = professionsData?.professions || []
  const progressSummary = progressSummaryData || []
  const professions = rawProfessions.map(p => {
    const summary = progressSummary.find(s => s.userProfessionId === p.id)
    return {
      ...p,
      ceRequirements: summary?.totalHoursRequired ? {
        totalHours: summary.totalHoursRequired,
        renewalPeriodMonths: 0,
      } : p.ceRequirements,
      progress: summary ? {
        totalHoursEarned: summary.totalHoursEarned,
        percentComplete: summary.percentComplete,
      } : p.progress,
      renewalCycleEndDate: summary?.renewalCycleEndDate || null,
      renewalCycleStartDate: summary?.renewalCycleStartDate || null,
      linkedCertificationName: summary?.linkedCertificationName || null,
    }
  })
  const ceRecords = ceRecordsData || []
  const isLoading = loadingProfessions || loadingRecords
  
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const filteredByProfession = professionFilter === 'all'
    ? ceRecords
    : ceRecords.filter(r => r.allocations?.some(a => a.userProfessionId === professionFilter))

  const sortedCERecords = [...filteredByProfession].sort((a, b) =>
    (a.title || '').localeCompare(b.title || '')
  )

  const openSelectionModal = (mode: 'export' | 'print') => {
    setProfessionFilter('all')
    setSelectedIds(new Set(ceRecords.map(c => c.id)))
    setSelectionMode(mode)
  }

  const handleProfessionFilterChange = (value: string) => {
    setProfessionFilter(value)
    const filtered = value === 'all'
      ? ceRecords
      : ceRecords.filter(r => r.allocations?.some(a => a.userProfessionId === value))
    setSelectedIds(new Set(filtered.map(c => c.id)))
  }

  const toggleSelection = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleSelectAll = () => {
    if (selectedIds.size === sortedCERecords.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(sortedCERecords.map(c => c.id)))
    }
  }

  const getSelectedRecords = () => sortedCERecords.filter(c => selectedIds.has(c.id))

  const handleExportCSV = () => {
    const selected = getSelectedRecords()
    const rows = [
      ['Title', 'Provider', 'Hours Awarded', 'Date Completed', 'Source', 'Notes'],
      ...selected.map(record => [
        record.title,
        record.provider || '',
        String(record.hoursAwarded || 0),
        record.dateCompleted ? new Date(record.dateCompleted).toLocaleDateString() : '',
        record.source || '',
        record.notes || ''
      ])
    ]
    const csvContent = rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `ce-records-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    URL.revokeObjectURL(url)
    setSelectionMode(null)
  }

  const handlePrint = async () => {
    setIsPreparing(true)
    const selected = getSelectedRecords()
    const imagePromises = selected.map(async (record) => {
      if (!record.documentUri) return { id: record.id, dataUrl: null }
      try {
        const res = await fetch(record.documentUri, {
          headers: getAuthHeaders(),
          credentials: 'include',
        })
        if (!res.ok) return { id: record.id, dataUrl: null }
        const blob = await res.blob()
        return new Promise<{ id: string; dataUrl: string | null }>((resolve) => {
          const reader = new FileReader()
          reader.onloadend = () => resolve({ id: record.id, dataUrl: reader.result as string })
          reader.onerror = () => resolve({ id: record.id, dataUrl: null })
          reader.readAsDataURL(blob)
        })
      } catch {
        return { id: record.id, dataUrl: null }
      }
    })

    const imageResults = await Promise.all(imagePromises)
    const imageMap = new Map(imageResults.map(r => [r.id, r.dataUrl]))

    const recordCards = selected.map(record => {
      const img = imageMap.get(record.id)
      const dataFields = `
        <div class="record-data">
          <h2>${record.title}</h2>
          <div class="field"><span class="label">Provider</span><span class="value">${record.provider || '—'}</span></div>
          <div class="field"><span class="label">Hours</span><span class="value">${record.hoursAwarded || 0}</span></div>
          <div class="field"><span class="label">Date Completed</span><span class="value">${record.dateCompleted ? new Date(record.dateCompleted).toLocaleDateString() : '—'}</span></div>
          <div class="field"><span class="label">Source</span><span class="value">${record.source || '—'}</span></div>
          ${record.notes ? `<div class="field"><span class="label">Notes</span><span class="value">${record.notes}</span></div>` : ''}
        </div>
      `
      if (img) {
        return `<div class="record-card has-image"><div class="record-image"><img src="${img}" alt="${record.title}" /></div>${dataFields}</div>`
      }
      return `<div class="record-card no-image">${dataFields}</div>`
    }).join('')

    const printContent = `
      <html>
        <head>
          <title>CE Records</title>
          <style>
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; padding: 24px; color: #111; }
            h1 { font-size: 22px; margin-bottom: 4px; }
            .subtitle { color: #666; font-size: 13px; margin-bottom: 24px; }
            .record-card { border: 1px solid #e5e7eb; border-radius: 12px; margin-bottom: 20px; overflow: hidden; page-break-inside: avoid; }
            .record-card.has-image { display: flex; }
            .record-image { width: 50%; flex-shrink: 0; background: #f9fafb; display: flex; align-items: center; justify-content: center; padding: 16px; }
            .record-image img { max-width: 100%; max-height: 400px; object-fit: contain; border-radius: 6px; }
            .record-card.has-image .record-data { width: 50%; }
            .record-card.no-image .record-data { width: 100%; }
            .record-data { padding: 20px; display: flex; flex-direction: column; justify-content: center; gap: 10px; }
            .record-data h2 { font-size: 17px; font-weight: 700; color: #111; margin-bottom: 4px; }
            .field { display: flex; justify-content: space-between; align-items: center; }
            .label { font-size: 13px; color: #6b7280; }
            .value { font-size: 13px; font-weight: 500; color: #111; }
            @media print { body { padding: 0; } .record-card { break-inside: avoid; } }
          </style>
        </head>
        <body>
          <h1>Continuing Education Records</h1>
          <p class="subtitle">Exported on ${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} &bull; ${selected.length} record${selected.length !== 1 ? 's' : ''}</p>
          ${recordCards}
        </body>
      </html>
    `
    const printWindow = window.open('', '_blank')
    if (printWindow) {
      printWindow.document.write(printContent)
      printWindow.document.close()
      setTimeout(() => {
        printWindow.focus()
        printWindow.print()
      }, 500)
    }
    setIsPreparing(false)
    setSelectionMode(null)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Continuing Education</h1>
          <p className="text-gray-500 mt-1">Track your CE hours and requirements</p>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => openSelectionModal('export')}
            disabled={ceRecords.length === 0}
            className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="button-export-ce-csv"
          >
            <Download className="w-4 h-4" />
            Export
          </button>
          <button
            onClick={() => openSelectionModal('print')}
            disabled={ceRecords.length === 0}
            className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="button-print-ce"
          >
            <Printer className="w-4 h-4" />
            Print
          </button>
          <div className="relative">
          <button
            onClick={() => setShowAddMenu(!showAddMenu)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Add CE Record
          </button>
          
          {showAddMenu && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowAddMenu(false)} />
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                <Link
                  to="/ce/new?mode=upload"
                  className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50"
                  onClick={() => setShowAddMenu(false)}
                >
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Upload className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Upload Certificate</p>
                    <p className="text-xs text-gray-500">Auto-extract from document</p>
                  </div>
                </Link>
                <Link
                  to="/ce/new?mode=manual"
                  className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50"
                  onClick={() => setShowAddMenu(false)}
                >
                  <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                    <Edit3 className="w-5 h-5 text-gray-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Enter Manually</p>
                    <p className="text-xs text-gray-500">Type in details yourself</p>
                  </div>
                </Link>
              </div>
            </>
          )}
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : professions.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Renewals Added</h3>
          <p className="text-gray-500 mb-6">
            Add a renewal to start tracking your CE requirements
          </p>
          <button
            onClick={() => setShowAddProfessionModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
          >
            <Plus className="w-5 h-5" />
            Add Renewal
          </button>
        </div>
      ) : (
        <>
          {/* Renewal Progress Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {professions.map((profession) => {
              const required = profession.ceRequirements?.totalHours || 0
              const earned = profession.progress?.totalHoursEarned || 0
              const percent = required > 0 ? Math.min((earned / required) * 100, 100) : 0
              
              return (
                <Link
                  key={profession.id}
                  to={`/ce/progress/${profession.id}`}
                  className="bg-white rounded-xl border border-gray-200 p-6 hover:border-blue-300 transition-colors"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {profession.professionName}
                      </h3>
                      <p className="text-sm text-gray-500">{profession.state}</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                  
                  <div className="mb-2">
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-gray-600">Progress</span>
                      <span className="font-medium text-gray-900">
                        {earned.toFixed(1)} / {required} hours
                      </span>
                    </div>
                    <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full transition-all ${
                          percent >= 100 ? 'bg-green-500' : percent >= 75 ? 'bg-blue-500' : 'bg-yellow-500'
                        }`}
                        style={{ width: `${percent}%` }}
                      />
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {percent >= 100 ? (
                        <span className="inline-flex items-center gap-1 text-sm text-green-600">
                          <CheckCircle className="w-4 h-4" />
                          Complete
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-sm text-gray-500">
                          <Clock className="w-4 h-4" />
                          {(required - earned).toFixed(1)} hours remaining
                        </span>
                      )}
                    </div>
                    {(profession.renewalCycleStartDate || profession.renewalCycleEndDate) && (
                      <span className="inline-flex items-center gap-1 text-xs text-indigo-600">
                        <Calendar className="w-3.5 h-3.5" />
                        {profession.renewalCycleStartDate && profession.renewalCycleEndDate
                          ? `${formatDate(profession.renewalCycleStartDate)} — ${formatDate(profession.renewalCycleEndDate)}`
                          : profession.renewalCycleEndDate
                            ? `Expires ${formatDate(profession.renewalCycleEndDate)}`
                            : `Started ${formatDate(profession.renewalCycleStartDate!)}`}
                      </span>
                    )}
                  </div>
                  {profession.linkedCertificationName && (
                    <div className="mt-1.5 flex items-center gap-1 text-xs text-gray-400">
                      <LinkIcon className="w-3 h-3" />
                      <span>{profession.linkedCertificationName}</span>
                    </div>
                  )}
                </Link>
              )
            })}
            
            {/* Add Profession Card */}
            <button
              onClick={() => setShowAddProfessionModal(true)}
              className="bg-white rounded-xl border-2 border-dashed border-gray-300 p-6 hover:border-blue-400 hover:bg-blue-50/50 transition-colors flex flex-col items-center justify-center min-h-[140px]"
            >
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                <Plus className="w-6 h-6 text-gray-400" />
              </div>
              <span className="text-sm font-medium text-gray-600">Add Another Renewal</span>
            </button>
          </div>

          {/* Recent CE Records */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent CE Records</h2>
            {ceRecords.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
                <p className="text-gray-500">No CE records yet. Add your first one!</p>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-200">
                {ceRecords.slice(0, 10).map((record) => (
                  <Link
                    key={record.id}
                    to={`/ce/${record.id}`}
                    className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="min-w-0">
                      <h3 className="text-sm font-medium text-gray-900 truncate">
                        {record.title}
                      </h3>
                      <p className="text-sm text-gray-500 truncate">
                        {record.provider}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        {record.dateCompleted ? formatDate(record.dateCompleted) : 'No date'}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0 ml-4">
                      <span className="inline-flex items-center px-2.5 py-1 bg-blue-100 text-blue-700 text-sm font-medium rounded-full">
                        {record.hoursAwarded || 0} hrs
                      </span>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      <AddProfessionModal
        isOpen={showAddProfessionModal}
        onClose={() => setShowAddProfessionModal(false)}
      />

      {selectionMode && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50" onClick={() => setSelectionMode(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {selectionMode === 'export' ? 'Export CE Records' : 'Print CE Records'}
              </h2>
              <button onClick={() => setSelectionMode(null)} className="p-1 hover:bg-gray-100 rounded-lg" data-testid="button-close-ce-selection">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="px-6 py-3 border-b border-gray-100">
              <p className="text-sm text-gray-500">All CE records are selected by default. Use the renewal filter to narrow by renewal, or uncheck individual records.</p>
            </div>

            {professions.length > 0 && (
              <div className="px-6 py-3 border-b border-gray-100">
                <label className="block text-xs font-medium text-gray-500 mb-1">Filter by Renewal</label>
                <select
                  value={professionFilter}
                  onChange={(e) => handleProfessionFilterChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="select-profession-filter"
                >
                  <option value="all">All Renewals</option>
                  {professions.map(p => (
                    <option key={p.id} value={p.id}>{p.professionName} — {p.state}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="px-6 py-3 border-b border-gray-100 flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer" data-testid="checkbox-ce-select-all">
                <input
                  type="checkbox"
                  checked={selectedIds.size === sortedCERecords.length && sortedCERecords.length > 0}
                  onChange={toggleSelectAll}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  {selectedIds.size === sortedCERecords.length ? 'Deselect All' : 'Select All'}
                </span>
              </label>
              <span className="text-sm text-gray-500">{selectedIds.size} of {sortedCERecords.length} selected</span>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-2 divide-y divide-gray-100">
              {sortedCERecords.length === 0 ? (
                <div className="py-8 text-center text-sm text-gray-500">
                  No CE records found{professionFilter !== 'all' ? ' for this renewal' : ''}.
                </div>
              ) : sortedCERecords.map(record => (
                <label key={record.id} className="flex items-center gap-3 py-3 cursor-pointer" data-testid={`checkbox-ce-${record.id}`}>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(record.id)}
                    onChange={() => toggleSelection(record.id)}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{record.title}</p>
                    <p className="text-xs text-gray-500 truncate">{record.provider || 'Unknown provider'}</p>
                  </div>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-600 flex-shrink-0">
                    {record.hoursAwarded || 0} hrs
                  </span>
                </label>
              ))}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
              {isPreparing && (
                <div className="flex items-center gap-2 text-sm text-gray-500 mr-auto">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                  Preparing print page...
                </div>
              )}
              <button
                onClick={() => setSelectionMode(null)}
                disabled={isPreparing}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={selectionMode === 'export' ? handleExportCSV : handlePrint}
                disabled={selectedIds.size === 0 || isPreparing}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid={`button-confirm-ce-${selectionMode}`}
              >
                {isPreparing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    Preparing...
                  </>
                ) : selectionMode === 'export' ? (
                  <><Download className="w-4 h-4" />Export {selectedIds.size} Record{selectedIds.size !== 1 ? 's' : ''}</>
                ) : (
                  <><Printer className="w-4 h-4" />Print {selectedIds.size} Record{selectedIds.size !== 1 ? 's' : ''}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
